import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Swords } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  team_a_id: "",
  team_b_id: "",
  league_a_id: "",
  league_b_id: "",
  date: "",
  home_goals: "",
  away_goals: "",
};

function teamLabel(t) {
  return t ? `${t.name} (${t.country})` : "—";
}

export default function ScoutMatchesPage() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, t, l] = await Promise.all([
        api.get("/matches"), api.get("/teams"), api.get("/leagues"),
      ]);
      setMatches(m.data); setTeams(t.data); setLeagues(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const teamsById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const canCreate = teams.length >= 2 && leagues.length >= 1;

  const openCreate = () => { setForm(emptyForm); setCreateOpen(true); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.team_a_id === form.team_b_id) { toast.error("Pick two different teams"); return; }
    setSaving(true);
    try {
      await api.post("/matches", {
        team_a_id: form.team_a_id,
        team_b_id: form.team_b_id,
        league_a_id: form.league_a_id,
        league_b_id: form.league_b_id || form.league_a_id,
        date: form.date,
        home_goals: form.home_goals === "" ? null : parseInt(form.home_goals, 10),
        away_goals: form.away_goals === "" ? null : parseInt(form.away_goals, 10),
      });
      toast.success("Match created"); setCreateOpen(false); await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const teamAOptions = teams.filter((t) => t.id !== form.team_b_id);
  const teamBOptions = teams.filter((t) => t.id !== form.team_a_id);
  const teamALeagues = teamsById[form.team_a_id]?.league_ids || [];
  const teamBLeagues = teamsById[form.team_b_id]?.league_ids || [];
  const leaguesFor = (ids) => leagues.filter((l) => ids.includes(l.id));

  return (
    <div data-testid="scout-matches-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Fixtures</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">Matches</h1>
          <p className="text-muted-foreground mt-2 max-w-lg">Submit and browse fixtures across all leagues.</p>
        </div>
        <Button onClick={openCreate} disabled={!canCreate} className="h-11 rounded-none font-display uppercase tracking-[0.18em]" data-testid="scout-create-match-button">
          <Plus className="h-4 w-4 mr-2" /> New Match
        </Button>
      </header>

      {!canCreate && !loading && (
        <div className="rounded-xl border border-border bg-card p-6 mb-6 text-sm text-muted-foreground">
          You need at least 2 teams and 1 league to log a match. Ask an admin to set them up.
        </div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <Table data-testid="scout-matches-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Fixture</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">League</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Date</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Score</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="px-6 py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : matches.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="px-6 py-16 text-center">
                <Swords className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <div className="font-display uppercase tracking-wider">No matches yet</div>
              </TableCell></TableRow>
            ) : (
              matches.map((m) => {
                const sameLeague = m.league_a_id === m.league_b_id;
                return (
                  <TableRow key={m.id} className="tr-fade border-border hover:bg-muted/30" data-testid={`scout-match-row-${m.id}`}>
                    <TableCell className="px-6 py-4">
                      <div className="font-medium">{m.team_a?.name || "Team A"} <span className="text-muted-foreground">vs</span> {m.team_b?.name || "Team B"}</div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm">
                      {sameLeague
                        ? (m.league_a?.name || "—")
                        : <span>{m.league_a?.name || "—"} <span className="text-muted-foreground">/</span> {m.league_b?.name || "—"}</span>}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-muted-foreground">{m.date}</TableCell>
                    <TableCell className="px-6 py-4 font-mono">
                      {m.home_goals == null && m.away_goals == null
                        ? <span className="text-muted-foreground">—</span>
                        : `${m.home_goals ?? "?"} – ${m.away_goals ?? "?"}`}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="scout-create-match-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">New Match</DialogTitle>
            <DialogDescription>Pick two teams; each has its own league for the fixture.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Team A</Label>
                <Select value={form.team_a_id} onValueChange={(v) => setForm({ ...form, team_a_id: v, league_a_id: "" })}>
                  <SelectTrigger data-testid="scout-match-team-a-select"><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>{teamAOptions.map((t) => <SelectItem key={t.id} value={t.id}>{teamLabel(t)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Team B</Label>
                <Select value={form.team_b_id} onValueChange={(v) => setForm({ ...form, team_b_id: v, league_b_id: "" })}>
                  <SelectTrigger data-testid="scout-match-team-b-select"><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>{teamBOptions.map((t) => <SelectItem key={t.id} value={t.id}>{teamLabel(t)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>League (Team A)</Label>
                <Select value={form.league_a_id} onValueChange={(v) => setForm({ ...form, league_a_id: v, league_b_id: form.league_b_id || v })} disabled={!form.team_a_id}>
                  <SelectTrigger data-testid="scout-match-league-a-select"><SelectValue placeholder={form.team_a_id ? "Select league" : "Pick Team A first"} /></SelectTrigger>
                  <SelectContent>{leaguesFor(teamALeagues).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>League (Team B)</Label>
                <Select value={form.league_b_id} onValueChange={(v) => setForm({ ...form, league_b_id: v })} disabled={!form.team_b_id}>
                  <SelectTrigger data-testid="scout-match-league-b-select"><SelectValue placeholder={form.team_b_id ? "Select league" : "Pick Team B first"} /></SelectTrigger>
                  <SelectContent>{leaguesFor(teamBLeagues).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2 col-span-3 sm:col-span-1">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="scout-match-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Home goals</Label>
                <Input type="number" min="0" value={form.home_goals} onChange={(e) => setForm({ ...form, home_goals: e.target.value })} placeholder="—" data-testid="scout-match-home-goals-input" />
              </div>
              <div className="space-y-2">
                <Label>Away goals</Label>
                <Input type="number" min="0" value={form.away_goals} onChange={(e) => setForm({ ...form, away_goals: e.target.value })} placeholder="—" data-testid="scout-match-away-goals-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={saving || !form.team_a_id || !form.team_b_id || !form.league_a_id || !form.league_b_id || !form.date}
                data-testid="scout-match-submit"
              >{saving ? "Saving…" : "Create match"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
